import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const EVENTS = [
  {
    id: "spring-release",
    title: "Spring Release Weekend",
    date: "April · Sat & Sun",
    desc: "New vintages poured for the first time, live music on the patio.",
    testid: "event-card-spring-release",
  },
  {
    id: "pickup-party",
    title: "Cellar Club Pick-Up Party",
    date: "Quarterly · Members",
    desc: "Reserve members collect their six bottles — barrel tastes, long tables, late light.",
    testid: "event-card-pickup-party",
  },
  {
    id: "farm-dinner",
    title: "Farm Dinner with Local Chefs",
    date: "September · Ticketed",
    desc: "One long table in the vines. Delta produce, cellar pairings, guest chefs.",
    testid: "event-card-farm-dinner",
  },
];

const RsvpModal = ({ event, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/events/rsvp", { ...form, event: event.title });
      toast.success(`You're on the list for ${event.title}.`);
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-[#0A0908]/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#141210] border border-[rgba(212,175,55,0.25)] p-8 relative"
      >
        <button data-testid="rsvp-modal-close" onClick={onClose} className="absolute top-4 right-4 text-[#6E685E] hover:text-[#F4F1EA]" aria-label="Close">
          <X size={18} />
        </button>
        <p className="overline-label">RSVP</p>
        <h3 className="font-serif-display text-3xl font-light text-[#F4F1EA] mt-3">{event.title}</h3>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E685E] mt-2">{event.date}</p>
        <form data-testid="rsvp-form" onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <input
            data-testid="rsvp-input-name"
            required
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors"
          />
          <input
            data-testid="rsvp-input-email"
            required
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors"
          />
          {error && <p data-testid="rsvp-error" className="text-sm text-[#C86D51]">{error}</p>}
          <button
            data-testid="rsvp-submit-btn"
            disabled={busy}
            className="mt-2 font-mono text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] py-4 hover:bg-[#F4F1EA] transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save My Seat"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export const Events = () => {
  const [rsvp, setRsvp] = useState(null);

  return (
    <section id="events" data-testid="events-section" className="relative py-28 lg:py-40 bg-[#0A0908]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading overline="Cellar & Farm" title="Gather with us." className="mb-16 lg:mb-24" />
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {EVENTS.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.1}>
              <div
                data-testid={ev.testid}
                className="spotlight-card group h-full bg-[#141210] border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.45)] p-8 flex flex-col transition-colors duration-500 hover:-translate-y-2 transform-gpu transition-transform"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{ev.date}</p>
                <h3 className="font-serif-display text-2xl lg:text-3xl font-light text-[#F4F1EA] mt-4">{ev.title}</h3>
                <p className="text-sm text-[#A39E93] mt-3 leading-relaxed flex-1">{ev.desc}</p>
                <button
                  data-testid={`event-rsvp-${ev.id}`}
                  onClick={() => setRsvp(ev)}
                  className="mt-8 self-start font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] border-b border-[#D4AF37]/40 pb-1 hover:text-[#F4F1EA] hover:border-[#F4F1EA] transition-colors"
                >
                  RSVP
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>{rsvp && <RsvpModal event={rsvp} onClose={() => setRsvp(null)} />}</AnimatePresence>
    </section>
  );
};
